import React, { useEffect, useState } from 'react';
import 'react-loading-skeleton/dist/skeleton.css';
import 'react-toastify/dist/ReactToastify.css';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import 'rc-checkbox/assets/index.css';
import './styles/variables.scss';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import 'react-image-gallery/styles/css/image-gallery.css';

import './styles/shared/reset.scss';
import './styles/shared/app.scss';
import './styles/shared/components.scss';
import './styles/shared/selection-controls.scss';
import './styles/shared/footer.scss';
import './styles/shared/header.scss';
import './styles/shared/generic.scss';
import './styles/shared/search.scss';
import './styles/shared/responsive.scss';
import './styles/shared/login.scss';
import './styles/shared/main.scss';
import './styles/shared/mo.scss';
import './styles/shared/accessibility.scss';
import './styles/shared/itinerary.scss';
import './styles/shared/proposal.scss';
import './styles/shared/trip-builder.scss';
import './styles/hotel/hotel.scss';
import './styles/hotel/results.scss';
import './styles/hotel/details.scss';
import './styles/hotel/book.scss';
import './styles/hotel/success.scss';
import './styles/hotel/map.scss';
import './styles/hotel/google.scss';
import './styles/hotel/hotel-modern.scss';
import './styles/shared/assistant.scss';
import './styles/transfer/transfer.scss';
import './styles/package/package.scss';
import './styles/flight/flight.scss';
import './styles/flight/flight-search.scss';
import './styles/flight/flight-details.scss';
import './styles/flight/fl-pair.scss';
import './styles/dynamic/dynamic.scss';
import './styles/brands.scss';
import './styles/media_1350.scss';

import { BrowserRouter } from 'react-router-dom';
import Header from 'components/Header';
import Footer from 'components/Footer';
import { AssistantWidget } from 'components/assistant';
import * as authorizeService from 'services/authorize';
import * as cognitoService from 'services/cognito';
import CookiePolicy from 'components/shared/CookiePolicy';
import { displayModes } from 'constants/constant';
import * as userService from 'services/user';
import { MainContext } from 'contexts/MainContext';
import { ToastContainer } from 'react-toastify';
import { i18nconfig } from 'utils/i18n';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import {
  cdnUrl,
  defaultUserSiteConfig,
  defaultHotelBookS3,
  baseUrl,
  maxInactiveMinutes,
} from 'constants/generic';
import {
  changeFavicon,
  clearIdb,
  configureStorage,
  initCSSVars,
  initHotjar,
  initLoader,
} from 'utils/site';
import { applyTheme, readTheme, themeId } from 'utils/theme';
import { Proposal } from 'ts/shared/interfaces/CreateProposalParams';
import { InitSiteData } from 'ts/shared/interfaces/CmsPageResponse';
import ScriptLoader from 'components/shared/ScriptLoader';
import { User } from 'ts/shared/interfaces/User';
import * as idb from 'utils/shared/idbUrl';
import { initSite } from 'services/cms';
import RouterConfig from 'components/RouterConfig';
import { getHotelBookS3 } from 'utils/s3';
import { getGlobalsConfig, GlobalsConfig } from 'services/config';
import CognitoLoginPage from 'components/CognitoLoginPage';
import ResetPassword from 'components/ResetPassword';
import { showWarning } from 'utils/utils';

function App() {
  const [isAppReady, setIsAppReady] = useState(false);
  const [user, setUser] = useState<User | undefined>();
  const [recaptchaKey, setRecaptchaKey] = useState('');
  const [isEnigma, setIsEnigma] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [loginAsUser, setLoginAsUser] = useState<User | undefined>();
  const [showCognitoLogin, setShowCognitoLogin] = useState(false);
  const [globals, setGlobals] = useState<GlobalsConfig | undefined>();
  const [initSiteData, setInitSiteDataState] = useState<InitSiteData>();
  const [proposal, setProposal] = useState<Proposal>({
    hotels: [],
    flights: [],
    dynamic: [],
    ilPackages: [],
    transfers: [],
  });

  const [userSiteConfig, setUserSiteConfig] = useState(defaultUserSiteConfig);
  const [hotelBookS3, setHotelBookS3] = useState(defaultHotelBookS3);
  const [discountHotelLimit, setDiscountHotelLimit] = useState(false);

  const setInitSiteData = (data?: InitSiteData) => {
    setInitSiteDataState(data);
    if (data) {
      localStorage.setItem('initSiteData', JSON.stringify(data));
    } else {
      localStorage.removeItem('initSiteData');
    }
  };

  useEffect(() => {
    (async () => {
      await configureStorage();

      await userService.configureAxiosBase(baseUrl);

      const newGlobals = await getGlobalsConfig();
      setGlobals(newGlobals);
      let newuser;
      if (newGlobals?.useBeproCognito) {
        if (localStorage.getItem('user') || window.location.search.includes('code')) {
          cognitoService.configureAxiosInterceptors(maxInactiveMinutes);
          if (newGlobals?.useAzureAD && !newGlobals.isManager) {
            newuser = await cognitoService.handleAzureCallback();
          } else {
            newuser = await cognitoService.handleBeproCognitoToken();
          }
          if (!newuser) {
            if (window.location.search) {
              window.location.replace(`${window.location.origin}${window.location.pathname}${window.location.hash}`);
              showWarning('Something went wrong. Please login again');
            }
          }
        } else {
          clearIdb();
          setIsAppReady(true);
          setShowCognitoLogin(true);
        }
      } else if (newGlobals?.useCognito) {
        if (localStorage.getItem('user') || window.location.search.includes('code')) {
          cognitoService.configureAxiosInterceptors(maxInactiveMinutes);
          newuser = await cognitoService.handleCognitoToken();
        } else {
          clearIdb();
          setIsAppReady(true);
          setShowCognitoLogin(true);
        }
      } else {
        clearIdb();
        newuser = await authorizeService.getUserToken();
        authorizeService.configureAxiosInterceptorsB2B();
      }

      if (!newuser) return;

      const siteData = (await initSite()) as any;
      if (siteData) {
        if (process.env.REACT_APP_DEBUG && siteData.other) {
          siteData.other.flightsAvailbale = 'true';
          siteData.other.packagesAvailable = 'true';
        }
        setInitSiteData(siteData);
        setRecaptchaKey(siteData.recapchaKey);
        initCSSVars(siteData);
        applyTheme(
          newuser?.config?.base?.theme ? themeId(newuser.config.base.theme) : readTheme()
        );
        setIsEnigma(siteData.companyId === 135);

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${siteData.apiKey}&libraries=places&language=en&loading=async`;
        document.head.appendChild(script);

        if (siteData.other) {
          setLogoUrl(siteData.other.logoUrl || require('./assets/img/bepro-logo.png'));
          if (siteData.other.siteTitle) {
            let addText = ' - BePro Travel';
            if ([109].includes(siteData.companyId)) {
              addText = '';
            }
            document.title = siteData.other.siteTitle + addText;
          }

          const isBranchLanguage = newuser?.branch?.ext_1_pcc?.toLowerCase().includes('display_lang');
          if (siteData.other.defaultLanguage || isBranchLanguage) {
            const lang = localStorage.getItem('language');
            if (!lang) {
              let defaultLanguage = siteData.other.defaultLanguage;

              if (isBranchLanguage) {
                const branchLangs = newuser?.branch?.ext_1_pcc.split(',');
                const language = branchLangs?.find((code: string) => code.toLowerCase().includes('display_lang'));
                if (language) {
                  defaultLanguage = language.replace(/display_lang:/i, '')?.toLowerCase();
                }
              }
              localStorage.setItem('language', defaultLanguage);
            }
          }

          if (siteData.other.faviconUrl) {
            changeFavicon(cdnUrl + siteData.other.faviconUrl);
          }

          setHotelBookS3(getHotelBookS3(siteData.other));
          setDiscountHotelLimit(siteData.other.discountHotelLimit === 'true');
        }
      }

      if (newuser?.config?.hotel) {
        const getValidDisplayMode = (displayMode?: number) =>
          displayModes.some((mode) => mode.value === displayMode) ? displayMode! : 7;
        setUserSiteConfig({
          ...newuser.config,
          hotel: {
            ...newuser.config.hotel,
            displayStyle: '2',
            areRoomsFiltered: false,
            displayMode: getValidDisplayMode(newuser.config.hotel?.displayMode),
          },
        });
      }

      const proposal = await idb.getProposal('proposal');
      if (proposal) {
        setProposal(proposal);
      }

      i18nconfig();
      setUser(newuser);
      if (localStorage.getItem('loginAsUser')) {
        setLoginAsUser(JSON.parse(localStorage.getItem('loginAsUser') || '{}'));
      }
      initLoader();

      setIsAppReady(true);
    })();
  }, []); // eslint-disable-line

  if (!isAppReady) return <div></div>;


  if (window.location.pathname.includes('/resetpassword')) return <ResetPassword />;

  if (showCognitoLogin) return <CognitoLoginPage globals={globals} />;

  return (
    <BrowserRouter>
      <div className="app">
        <GoogleReCaptchaProvider
          reCaptchaKey={recaptchaKey}
          useEnterprise={false}
          useRecaptchaNet={true}
          scriptProps={{
            async: true,
            defer: true,
            appendTo: 'head',
            nonce: undefined,
          }}
          container={{
            element: 'recaptcha-badge',
            parameters: {
              badge: 'inline',
            },
          }}
        >
          <div id="recaptcha-badge" className="footer-recaptcha is-hidden" />
          <ScriptLoader>
            <MainContext.Provider
              value={{
                proposal,
                setProposal,
                user,
                setUser,
                setUserSiteConfig,
                userSiteConfig,
                loginAsUser,
                setLoginAsUser,
                globals,
                initSiteData,
                setInitSiteData,
                cdnUrl,
                isEnigma,
                discountHotelLimit,
                showOrderCompanyId: isEnigma,
                showOrderTripId: isEnigma,
                bookConfig: hotelBookS3,
              }}
            >
              <Header logoUrl={logoUrl} />
              <RouterConfig />
              <AssistantWidget
                hideOnPaths={['/login', '/resetpassword', '/proposal/trip-builder']}
              />
              <CookiePolicy policyLink="/" onAccept={initHotjar} />
              <Footer logoUrl={logoUrl} />
            </MainContext.Provider>
          </ScriptLoader>
        </GoogleReCaptchaProvider>
      </div>
      <ToastContainer />
    </BrowserRouter>
  );
}

export default App;
